import { AllowFor } from 'modules/app/components/atoms/feature-flags/AllowFor';
import { getProposalOverviewOptions, SecurityAccessFeaturesEnum } from 'bff';
import style from './proposal-overview.module.scss';
import {
  ITableSortLabel,
  TabGroup,
  TableCell,
  TablePagination,
  TableSortLabel,
  DisabledProvider,
  Typography,
  Paper,
  ChoiceChip,
  ChoiceChipGroup
} from '@sanlam-glacier/app-common/components';
import { Table, TableBody, TableHeader } from 'components/organisms/table';
import { ProposalTableRow } from 'modules/new-business/components/organisms/proposal-table-row/ProposalTableRow';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { proposalOverviewHeaderData } from './proposal-overview.function';
import { ProposalSkeletonLoader } from 'modules/new-business/components/organisms/proposal-skeleton-loader/ProposalSkeletonLoader';
import { CreateNewProposalButton } from 'modules/new-business/components/molecules/create-new-proposal-button/CreateNewProposalButton';
import { NoProposals } from 'modules/new-business/components/organisms/no-proposals/NoProposals';
import { EmptyProposals } from 'modules/new-business/components/organisms/empty-proposals/EmptyProposals';
import { ProposalFilterType } from 'bff/client/models/ProposalFilterType';
import { IAPPROPOSALPROGRESS } from 'bff/client/models/IAPPROPOSALPROGRESS';
import { MobileViewProposals } from 'modules/new-business/components/organisms/mobile-view-proposals/MobileViewProposals';
import { useGetBreakPointReached } from 'hooks/useGetBreakPointReached';
import { EmptyData } from '../empty-data/EmptyData';
import { environmentService } from 'stores/store';
import { SearchBar } from 'components/molecules/search-bar/SearchBar';
import { proposalSearchMaxhLength } from 'utils/validations/validation-limits';

export const ProposalOverview = () => {
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState<string>();
  const [order, setOrder] = useState<ITableSortLabel['direction']>(TableSortLabel.DIRECTION.DESC);
  const [orderBy, setOrderBy] = useState('id');
  const [typeFilter, setTypeFilter] = useState<ProposalFilterType | undefined>();
  const [progressFilter, setProgressFilter] = useState<IAPPROPOSALPROGRESS | undefined>();

  const mobileBreakPoint = environmentService.getMobileBreakPoint();
  const mobileView = useGetBreakPointReached(mobileBreakPoint);

  const debouncedHandleSearch = (value?: string, _isFocused?: boolean) => {
    value = value ?? '';
    setPage(0);
    value = value.trim();
    const shouldUpdate = value.length >= 3 || value.length === 0;
    shouldUpdate && setSearch(value);
  };

  const handleSort = (propertyName: string | undefined) => {
    if (!propertyName) return;
    if (propertyName === orderBy) {
      setOrder(
        order === TableSortLabel.DIRECTION.ASC
          ? TableSortLabel.DIRECTION.DESC
          : TableSortLabel.DIRECTION.ASC
      );
    } else {
      setOrder(TableSortLabel.DIRECTION.DESC);
      setOrderBy(propertyName);
    }
    setPage(0);
  };

  const handleTypeFilterChange = (value: string) => {
    setPage(0);
    if (value === 'all') {
      setTypeFilter(undefined);
      return;
    }
    setTypeFilter(value as ProposalFilterType);
  };

  const handleProgressFilterChange = (_e: React.SyntheticEvent, value: string | string[]) => {
    setPage(0);
    const newValue = value.toString();
    if (newValue === 'all') {
      setProgressFilter(undefined);
      return;
    }
    setProgressFilter(newValue as IAPPROPOSALPROGRESS);
  };

  const {
    data: proposalOverviewData,
    isLoading,
    isFetching
  } = useQuery(
    getProposalOverviewOptions({
      query: {
        orderBy,
        sortDirection: order === 'asc' ? 'Ascending' : 'Descending',
        typeFilter,
        progressFilter,
        pagingArgs: {
          pageNumber: page + 1,
          pageSize: pageSize,
          skipAmount: page * pageSize,
          usePaging: true
        },
        searchTerm: search
      }
    })
  );

  const isEmptyData =
    proposalOverviewData?.count === 0 || proposalOverviewData?.count === undefined;
  const doesNotHaveAnyProposals =
    isEmptyData && !search && !typeFilter && !progressFilter && !isFetching;

  return (
    <ProposalSkeletonLoader loading={isLoading}>
      <div className={style.overviewHeaderSection}>
        <Typography
          align={Typography.ALIGN.LEFT}
          variant={Typography.VARIANT.H2}
          weight={Typography.WEIGHT.MEDIUM}
        >
          New business overview
        </Typography>
        <AllowFor features={[SecurityAccessFeaturesEnum.CreateProposal]}>
          <CreateNewProposalButton alignEnd />
        </AllowFor>
      </div>

      <div className={style.overviewSubHeaderSection}>
        <DisabledProvider value={doesNotHaveAnyProposals}>
          <TabGroup
            defaultSelected='all'
            onChange={handleTypeFilterChange}
            panelPadding
            position={TabGroup.POSITION.LEFT}
            tabs={[
              {
                label: 'All',
                name: 'all'
              },
              {
                label: 'Proposals',
                name: ProposalFilterType.Proposal
              },
              {
                label: 'Applications',
                name: ProposalFilterType.Application
              },
              {
                label: 'Signing',
                name: ProposalFilterType.Signing
              }
            ]}
          />
          <SearchBar
            searchClassName={style.searchBar}
            handleSearch={debouncedHandleSearch}
            isLoading={!!search && (isLoading || isFetching)}
            searchTitle='Search proposals'
            maxSearchLength={proposalSearchMaxhLength}
          />
        </DisabledProvider>
      </div>
      {doesNotHaveAnyProposals ? (
        <NoProposals />
      ) : (
        <>
          <Paper>
            <ChoiceChipGroup
              className={style.choiceFilters}
              defaultSelected={'all'}
              onChange={handleProgressFilterChange}
            >
              <ChoiceChip aria-label={'All'} value={'all'}>
                All
              </ChoiceChip>
              <ChoiceChip aria-label={'In Progress'} value={IAPPROPOSALPROGRESS.InProgress}>
                In progress
              </ChoiceChip>
              <ChoiceChip aria-label={'Completed'} value={IAPPROPOSALPROGRESS.Complete}>
                Completed
              </ChoiceChip>
            </ChoiceChipGroup>
            {!mobileView && (
              <Table>
                <TableHeader
                  hoverable={false}
                  disabled={isFetching}
                  headers={proposalOverviewHeaderData}
                  orderBy={orderBy}
                  align={TableCell.ALIGN.LEFT}
                  direction={order}
                  handleSort={(propertyName) => handleSort(propertyName)}
                  tableHeaderClassName={style.tableSortHeaderLabel}
                />
                {isEmptyData ? (
                  <EmptyProposals
                    search={search}
                    numberOfColumns={proposalOverviewHeaderData.length}
                  />
                ) : (
                  <TableBody
                    rows={proposalOverviewData.data ?? []}
                    renderRow={(row) => (
                      <ProposalTableRow key={row.id} row={row} disabled={isFetching} />
                    )}
                  />
                )}
              </Table>
            )}
            {mobileView && (
              <>
                {!isEmptyData && (
                  <MobileViewProposals
                    search={search}
                    proposals={proposalOverviewData?.data}
                    disabled={isFetching}
                  />
                )}
                {isEmptyData && <EmptyData searchTerm={search} />}
              </>
            )}
            {!isEmptyData && (
              <TablePagination
                className={style.tableFooter}
                count={proposalOverviewData?.count}
                rowsPerPage={pageSize}
                page={page}
                onPageChange={(value) => setPage(value)}
                onRowsPerPageChange={(value) => {
                  setPage(0);
                  setPageSize(value);
                }}
                rowsPerPageOptions={[5, 10, 25, 50]}
                dropdownLabel={mobileView ? 'Cards per page:' : 'Rows per page:'}
                closeMenuOnSelection
                maxVisibleItems={5}
              />
            )}
          </Paper>
        </>
      )}
    </ProposalSkeletonLoader>
  );
};
